import { InboxOutlined } from '@ant-design/icons'
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import type { UploadProps } from 'antd'
import { Upload } from 'antd'
import { UploadFile } from 'antd/es/upload/interface'
import { RcFile } from 'antd/lib/upload'
import { memo, useContext, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Input, UploadInput } from 'src/components/inputs'
import { downloadTemplate, validateExcel } from 'src/templates/template-goals'
import { AuthContext } from '../Auth/AuthProvider'
import { Button } from '../buttons/Button'
import PageHeader from '../common/PageHeader'
export interface UploadGoalFormData {
  title: string
  description: string
  thumbnail: UploadFile[]
  thumbnailUrl?: string
  published: string
}

const { Dragger } = Upload

const UploadGoal = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const form = useForm<UploadGoalFormData>()
  const isLibraryPage = window.location.pathname.includes('it-goal-starter')
  const [error, setError] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const { userInfo } = useContext(AuthContext)
  const [file, setFile] = useState<File | null>(null)

  const uploadUrl = isLibraryPage ? '/goals/starter' : '/goals/excel'

  const props: UploadProps = {
    name: 'file',
    accept: '.xlsx,.xls',
    multiple: false,
    maxCount: 1,
    beforeUpload: () => false,
    onChange: async (info) => {
      const selectedFile = info.fileList[0]?.originFileObj || null
      setFile(selectedFile)
      if (selectedFile) {
        const errors = await validateExcel(selectedFile)
        setError(errors)
      } else {
        setError([])
      }
    },
    onDrop(e) {
      console.log('Dropped files', e.dataTransfer.files)
    },
  }

  const errorTable = useReactTable({
    data: error,
    columns: [
      {
        accessorKey: 'row',
        header: 'Row',
      },
      {
        accessorKey: 'error',
        header: 'Error',
        cell: (info) => (
          <span
            dangerouslySetInnerHTML={{
              __html: info.getValue().replace(/\n/g, '<br />'),
            }}
            style={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}
          />
        ),
      },
    ],
    getCoreRowModel: getCoreRowModel(),
  })

  const handleUpload = async (values?: UploadGoalFormData) => {
    try {
      setLoading(true)

      const formData = new FormData()

      formData.append('file', file as File)
      if (isLibraryPage && values) {
        const { title, description = '', thumbnail } = values

        if (!thumbnail || thumbnail.length === 0) {
          toast.error(t('common.toast.error'))
          return
        }

        formData.append('title', title)
        formData.append('thumbnail', thumbnail[0]?.originFileObj as RcFile)
        formData.append('description', description)
      } else {
        formData.append('user_id', userInfo?.stsUserId || '')
        formData.append(
          'organisation_id',
          userInfo?.organization?.stsOrganisationId || '',
        )
      }

      const carchResponse = await fetch(
        `${process.env.REACT_APP_CARCH_ENDPOINT}${uploadUrl}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
          body: formData,
        },
      )
      if (!carchResponse.ok) {
        toast.error(t('common.toast.error'))
        return
      }

      toast.success(
        t('notification.resource.create', {
          name: t('common.word.goal'),
        }),
      )
      history.push(isLibraryPage ? '/it-goal-starter' : '/goals')
    } catch (error) {
      toast.error(t('common.toast.error'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="container-xxl">
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleUpload)}>
          <PageHeader
            showBackButton
            headerTitle={t('common.button.uploadGoal')}
            renderRight={() => (
              <div className="d-flex justify-content-end gap-2 mt-3">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    history.goBack()
                  }}
                >
                  {t('common.button.cancel')}
                </button>
                <button
                  className="btn btn-primary"
                  disabled={error.length > 0 || !file || loading}
                  type="submit"
                  onClick={form.handleSubmit(handleUpload)}
                >
                  {t('common.button.save')}
                </button>
              </div>
            )}
          />

          <div className="card">
            <div className="card-body">
              <h6>{t('resource.goal.field.uploadBody')}</h6>

              <Button
                className="mb-3"
                label={t('common.button.downloadTemplate', 'Download Template')}
                type="button"
                onClick={downloadTemplate}
              />
              <Dragger {...props}>
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  {t('resource.goal.field.uploadFile')}
                </p>
              </Dragger>
              {error.length > 0 && (
                <div className="mt-4">
                  <h6 style={{ color: 'red' }}>Validation Errors</h6>
                  <table className="table table-bordered">
                    <thead>
                      {errorTable.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id}>
                          {headerGroup.headers.map((header) => (
                            <th key={header.id}>
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody>
                      {errorTable.getRowModel().rows.map((row) => (
                        <tr key={row.id}>
                          {row.getVisibleCells().map((cell) => (
                            <td key={cell.id}>
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {isLibraryPage && (
                <>
                  <div className="mt-3">
                    <Input
                      required
                      label={'common.word.title'}
                      source="title"
                    />
                  </div>
                  <div className="mt-3">
                    <Input
                      autoSize={{ minRows: 3, maxRows: 5 }}
                      label={'common.word.description'}
                      source="description"
                    />
                  </div>
                  <div className="mt-3">
                    <UploadInput
                      required
                      accept="image/*"
                      label="common.word.thumbnail"
                      maxCount={1}
                      source="thumbnail"
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  )
}

export default memo(UploadGoal)
